import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Play, GitFork, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const asset = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);

function Node({ n, tone, onPlay, onRecenter, current }) {
  const ring = current ? "border-fuchsia-500 ring-2 ring-fuchsia-500/40" : tone === "up" ? "border-cyan-500/40" : "border-[hsl(var(--border))]";
  return (
    <div data-testid={`tree-node-${n.id}`} className={`flex items-center gap-3 rounded-xl border-2 ${ring} bg-[hsl(var(--card))] p-2.5`}>
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-[hsl(var(--secondary))] grid place-items-center shrink-0">
        {n.cover_image ? <img src={asset(n.cover_image)} alt="" className="w-full h-full object-cover" loading="lazy" /> : <span className="text-2xl">{n.cover_emoji}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate flex items-center gap-1.5">
          {current && <MapPin className="w-3.5 h-3.5 text-fuchsia-500 shrink-0" />}{n.title}
        </div>
        <div className="text-[11px] text-[hsl(var(--muted-foreground))]">by {n.owner_name} · ▶ {n.plays} · {n.dimension === "3d" ? "3D" : "2D"}</div>
      </div>
      {!current && (
        <Button size="sm" variant="ghost" className="rounded-full h-8 px-2 shrink-0" onClick={() => onRecenter(n)} data-testid={`tree-recenter-${n.id}`} title="Center tree here">
          <GitFork className="w-4 h-4" />
        </Button>
      )}
      {n.playable && (
        <Button size="sm" variant="outline" className="rounded-full h-8 px-3 shrink-0" onClick={() => onPlay(n)} data-testid={`tree-play-${n.id}`}>
          <Play className="w-3.5 h-3.5" /> Play
        </Button>
      )}
    </div>
  );
}

export default function RemixTree({ gameId, open, onOpenChange }) {
  const [rootId, setRootId] = useState(gameId);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { if (open) setRootId(gameId); }, [open, gameId]);
  useEffect(() => {
    if (!open || !rootId) return;
    setData(null);
    api.get(`/mashup/${rootId}/lineage`).then((r) => setData(r.data)).catch(() => setData({ ancestors: [], node: null, children: [] }));
  }, [open, rootId]);

  const play = (n) => { onOpenChange(false); navigate(`/app/arcade/${n.id}`); };
  const recenter = (n) => setRootId(n.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="remix-tree-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitFork className="w-5 h-5 text-fuchsia-500" /> Remix family tree</DialogTitle>
          <DialogDescription>Trace where this mashup came from and everything it inspired. Tap a fork to re-center, or Play any published version.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))]" /></div>
        ) : !data.node ? (
          <div className="text-center py-12 text-sm text-[hsl(var(--muted-foreground))]">Couldn't load this tree.</div>
        ) : (
          <div className="relative">
            {data.ancestors.length > 0 && (
              <div className="text-[11px] font-bold uppercase tracking-wide text-cyan-500 mb-2">Remixed from</div>
            )}
            {data.ancestors.map((n) => (
              <div key={n.id} className="relative pl-7 pb-4" data-testid={`tree-anc-${n.id}`}>
                <span className="absolute left-[11px] top-8 -bottom-4 w-0.5 bg-cyan-500/50" />
                <span className="absolute left-[11px] top-8 w-4 h-0.5 bg-cyan-500/50" />
                <Node n={n} tone="up" current={false} onPlay={play} onRecenter={recenter} />
              </div>
            ))}
            <div className="relative pl-7">
              {data.ancestors.length > 0 && <span className="absolute left-[11px] top-8 w-4 h-0.5 bg-fuchsia-500/60" />}
              <Node n={data.node} current onPlay={play} onRecenter={recenter} />
            </div>
            {data.children.length > 0 && (
              <div className="relative pl-7 mt-4">
                <div className="text-[11px] font-bold uppercase tracking-wide text-fuchsia-500 mb-2">Remixes ({data.children.length})</div>
                <span className="absolute left-[11px] top-0 bottom-8 w-0.5 bg-fuchsia-500/50" />
                <div className="space-y-3">
                  {data.children.map((n) => (
                    <div key={n.id} className="relative">
                      <span className="absolute left-[-16px] top-8 w-4 h-0.5 bg-fuchsia-500/50" />
                      <Node n={n} tone="down" current={false} onPlay={play} onRecenter={recenter} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.ancestors.length === 0 && data.children.length === 0 && (
              <div className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]" data-testid="tree-empty">This mashup is an original — no remixes yet. Be the first to remix it!</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
